import { Diagram } from './diagrams'
import { HelpCallout } from './HelpCallout'
import { Inline } from './Inline'
import { headingId } from './search'
import type { HelpBlock } from './types'

function Heading({ text }: { text: string }) {
  return (
    <h3
      id={headingId(text)}
      className="mt-4 scroll-mt-24 font-display text-[18px] font-semibold tracking-[-0.01em] text-ink lg:scroll-mt-6"
    >
      {text}
    </h3>
  )
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="ml-5 flex list-disc flex-col gap-2 marker:text-teal-300">
      {items.map((item) => (
        <li key={item} className="pl-1">
          <Inline text={item} />
        </li>
      ))}
    </ul>
  )
}

/** Numbered steps with a large numeral, the Help centre's signature block. */
function Steps({ items }: { items: string[] }) {
  return (
    <ol className="help-steps flex flex-col gap-3">
      {items.map((item, index) => (
        <li key={item} className="flex gap-3.5">
          <span
            aria-hidden="true"
            className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-teal font-display text-[13px] font-semibold text-white"
          >
            {index + 1}
          </span>
          <span className="sr-only">Step {index + 1}: </span>
          <span className="min-w-0 pt-0.5">
            <Inline text={item} />
          </span>
        </li>
      ))}
    </ol>
  )
}

interface TableProps {
  caption?: string
  head: string[]
  rows: string[][]
}

/** Below 640 px each row becomes a small stacked block instead of a squeezed table. */
function StackedRows({ caption, head, rows }: TableProps) {
  return (
    <div className="sm:hidden print:hidden">
      {caption ? (
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-sage">
          {caption}
        </p>
      ) : null}
      <ul className="divide-y divide-line-soft border-y border-line-soft text-sm">
        {rows.map((row) => (
          <li key={row.join('|')} className="py-3">
            <p className="font-medium text-ink break-words">
              <Inline text={row[0] ?? ''} />
            </p>
            <dl className="mt-1.5 flex flex-col gap-2 text-ink-soft">
              {row.slice(1).map((cell, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
                <div key={index} className="break-words">
                  <dt className="text-xs font-semibold text-sage">{head[index + 1]}</dt>
                  <dd>
                    <Inline text={cell} />
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Table({ caption, head, rows }: TableProps) {
  return (
    <>
      <StackedRows caption={caption} head={head} rows={rows} />
      <div className="help-table hidden overflow-x-auto sm:block print:block">
        <table className="w-full border-collapse text-left text-sm">
          {caption ? (
            <caption className="mb-2 text-left text-xs font-semibold uppercase tracking-[0.08em] text-sage">
              {caption}
            </caption>
          ) : null}
          <thead>
            <tr className="border-b border-line-strong">
              {head.map((cell) => (
                <th key={cell} scope="col" className="px-3 py-2 font-semibold text-ink first:pl-0">
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.join('|')} className="border-b border-line-soft align-top">
                {row.map((cell, index) =>
                  index === 0 ? (
                    <th
                      key={`h-${cell}`}
                      scope="row"
                      className="py-2.5 pr-3 font-medium text-ink break-words"
                    >
                      <Inline text={cell} />
                    </th>
                  ) : (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
                      key={index}
                      className="px-3 py-2.5 text-ink-soft break-words"
                    >
                      <Inline text={cell} />
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/** "What you see / What to do" pairs. */
function SeeDo({ caption, rows }: { caption?: string; rows: [string, string][] }) {
  return (
    <div className="help-seedo overflow-hidden rounded-lg border border-line">
      {caption ? (
        <p className="border-b border-line bg-wash px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-sage">
          {caption}
        </p>
      ) : null}
      <dl className="divide-y divide-line-soft">
        {rows.map(([see, action]) => (
          <div
            key={see}
            className="grid gap-1.5 px-4 py-3 sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] sm:gap-6"
          >
            <dt className="text-sm font-semibold text-ink">
              <span className="mr-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-teal-500 sm:sr-only">
                What you see
              </span>
              <Inline text={see} />
            </dt>
            <dd className="text-sm leading-relaxed text-ink-soft">
              <span className="mr-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-teal-500 sm:sr-only">
                What to do
              </span>
              <Inline text={action} />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function Terms({ items }: { items: { term: string; text: string }[] }) {
  return (
    <dl className="divide-y divide-line-soft">
      {items.map((item) => (
        <div
          key={item.term}
          className="grid gap-1 py-3.5 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] sm:gap-6"
        >
          <dt
            id={headingId(item.term)}
            className="scroll-mt-24 font-display text-[15px] font-semibold text-ink lg:scroll-mt-6"
          >
            {item.term}
          </dt>
          <dd className="text-[15px] leading-relaxed text-ink-soft">
            <Inline text={item.text} />
          </dd>
        </div>
      ))}
    </dl>
  )
}

function Faq({ items }: { items: { q: string; a: string }[] }) {
  return (
    <div className="flex flex-col divide-y divide-line-soft">
      {items.map((item) => (
        <div key={item.q} className="py-4 first:pt-0">
          <h3
            id={headingId(item.q)}
            className="scroll-mt-24 font-display text-[16px] font-semibold text-ink lg:scroll-mt-6"
          >
            {item.q}
          </h3>
          <p className="mt-1.5 text-[15px] leading-relaxed text-ink-soft">
            <Inline text={item.a} />
          </p>
        </div>
      ))}
    </div>
  )
}

export function HelpBlockView({ block }: { block: HelpBlock }) {
  switch (block.type) {
    case 'p':
      return (
        <p>
          <Inline text={block.text} />
        </p>
      )
    case 'h':
      return <Heading text={block.text} />
    case 'list':
      return <BulletList items={block.items} />
    case 'steps':
      return <Steps items={block.items} />
    case 'seedo':
      return <SeeDo caption={block.caption} rows={block.rows} />
    case 'table':
      return <Table caption={block.caption} head={block.head} rows={block.rows} />
    case 'callout':
      return (
        <HelpCallout kind={block.kind} title={block.title}>
          {block.text ? (
            <p>
              <Inline text={block.text} />
            </p>
          ) : null}
          {block.items ? (
            <ul>
              {block.items.map((item) => (
                <li key={item}>
                  <Inline text={item} />
                </li>
              ))}
            </ul>
          ) : null}
        </HelpCallout>
      )
    case 'diagram':
      return <Diagram id={block.id} />
    case 'terms':
      return <Terms items={block.items} />
    case 'faq':
      return <Faq items={block.items} />
  }
}

/** Prose keeps a readable measure; tables, pairs and diagrams may use the full width. */
export function isWide(block: HelpBlock): boolean {
  return (
    block.type === 'table' ||
    block.type === 'seedo' ||
    block.type === 'diagram' ||
    block.type === 'terms'
  )
}
